import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, forkJoin, from, map, of } from 'rxjs';
import { BulkResponse, PipeSchedule, ProductFamily, SBU, SealingElement, SealingElementPluggingHead, SellableStatus, SubSBU } from '../models/sealing-element.model';
import { ENV_CONFIG, EnvironmentConfig } from 'src/environments/environment.model';
import { LookupValue } from '../models/lookup.model';
import { SealingElementFilter, SealingElementMaintenanceFilter } from '../store/fromSealingElement.barrel';
import { BinaryReaderService } from './binary-reader.service';


@Injectable({
  providedIn: 'root'
})
export class SealingElementsService {
  endpoint: { url: string; version: string; };
  local: { url: string; version: string; };

  constructor(
    private http: HttpClient,
    @Inject(ENV_CONFIG) private config: EnvironmentConfig,
    private binaryService: BinaryReaderService) {
    this.endpoint = this.config.endpoints.sealingElementsApi;
    this.local = this.config.endpoints.local;
  }

  get(q: string): Observable<BulkResponse<SealingElement>> {
    return this.http.get<BulkResponse<SealingElement>>(`${this.endpoint?.url}api/${this.endpoint.version}/SealingElements${q}`);
  }

  /// This gets only the active values for the pipesize lookup
  getPipeSizesForFilter(): Observable<LookupValue[]> {
    return this.http.get<LookupValue[]>(`${this.endpoint.url}api/${this.endpoint.version}/PipeSizes`);
  }

  getMaterialsForFilter(): Observable<LookupValue[]> {
    return this.http.get<LookupValue[]>(`${this.endpoint.url}api/${this.endpoint.version}/Materials`);
  }

  getProductFamiliesForFilter(): Observable<ProductFamily[]> {
    return this.http.get<ProductFamily[]>(`${this.endpoint.url}api/${this.endpoint.version}/ProductFamilies`);
  }

  getSbusForFilter(): Observable<SBU[]> {
    return this.http.get<SBU[]>(`${this.endpoint.url}api/${this.endpoint.version}/SBUs`);
  }

  getSubSBUsForFilter(): Observable<SubSBU[]> {
    return this.http.get<SubSBU[]>(`${this.endpoint.url}api/${this.endpoint.version}/SubSBUS`);
  }

  /// This gets only the active values for the categories lookup
  getCategoriesForFilter(): Observable<LookupValue[]> {
    return this.http.get<LookupValue[]>(`${this.endpoint.url}api/${this.endpoint.version}/Categories`);
  }

  getSellablesForFilter(): Observable<SellableStatus[]> {
    return this.http.get<SellableStatus[]>(`${this.endpoint.url}api/${this.endpoint.version}/Sellables`);
  }

  /// This gets only the active values for the product codes lookup
  getProductCodesForFilter(): Observable<LookupValue[]> {
    return this.http.get<LookupValue[]>(`${this.endpoint.url}api/${this.endpoint.version}/ProductCodes`);
  }

  getEquipmentTypesForFilter(): Observable<LookupValue[]> {
    return this.http.get<LookupValue[]>(`${this.endpoint.url}api/${this.endpoint.version}/EquipmentTypes`);
  }

  getANSIPipeSizes(): Observable<any> {
    return this.http.get<PipeSchedule[]>(`${this.local.url}data/ansi-pipe-schedules.json`);
  }

  getPluggingHeads(): Observable<SealingElementPluggingHead[]> {
    return this.http.get<SealingElementPluggingHead[]>(`${this.endpoint.url}api/v2/ProductPluggingHeads`);
  }

  extractDataFromExcelTemplate(file: File): Observable<any> {
    const xform = new FormData();
    xform.append('file', file);
    const headers = new HttpHeaders();
    headers.append('enctype', 'multipart/form-data')
    return this.http.post(`${this.endpoint.url}api/v2/SealingElementsBulk`, xform, { headers });
  }

  exportToExcel(selection: string[]): Observable<Blob> {
    const q = selection.map(v => `partNumbers=${v}`).join('&');
    console.log(q);

    return this.http.get<ArrayBuffer>(`${this.endpoint.url}api/v2/SealingElementsBulk?${q}`, { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/octet-stream' })));
  }
}
